import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { disablePush, enablePush, pushState, type PushState } from "@/lib/push";
import { usePrefs } from "@/contexts/PrefsContext";

export const PushToggle = ({ compact = false }: { compact?: boolean }) => {
  const { t } = usePrefs();
  const [st, setSt] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushState().then(setSt).catch(() => setSt("unsupported")); }, []);
  if (!st || (compact && st !== "off")) return null;

  const run = async (fn: () => Promise<PushState>) => {
    setBusy(true);
    try {
      const s = await fn();
      setSt(s);
      if (s === "on") toast.success(t("push.enabled"));
      if (s === "denied") toast.error(t("push.denied"));
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  const test = () => api.post("/push/test").then(() => toast.success(t("push.testSent"))).catch(async (e) => {
    toast.error(errorText(e, t));
    if (e?.response?.status === 409) setSt(await disablePush());
  });

  if (compact)
    return (
      <button onClick={() => run(enablePush)} disabled={busy} data-testid="push-enable-compact"
        className="w-full rounded-2xl bg-secondary/15 text-secondary-foreground px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-secondary/25 transition-[background-color]">
        <Smartphone className="h-4 w-4" />{t("push.compact")}
      </button>
    );

  return (
    <section data-testid="push-card" className="rounded-3xl border bg-card p-5 space-y-3">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary/15 shrink-0">{st === "on" ? <BellRing className="h-5 w-5 text-emerald-600" /> : <BellOff className="h-5 w-5" />}</span>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold">{t("push.title")}</h2>
          <p data-testid="push-status" className="text-sm text-muted-foreground">{t(`push.st.${st}`)}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {st === "off" && <button onClick={() => run(enablePush)} disabled={busy} data-testid="push-enable-button" className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold active:scale-95 transition-transform">{t("push.enable")}</button>}
        {st === "on" && <>
          <button onClick={test} data-testid="push-test-button" className="h-10 px-5 rounded-full bg-secondary/20 text-sm font-bold active:scale-95 transition-transform">{t("push.test")}</button>
          <button onClick={() => run(disablePush)} disabled={busy} data-testid="push-disable-button" className="h-10 px-5 rounded-full border text-sm font-bold active:scale-95 transition-transform">{t("push.disable")}</button>
        </>}
      </div>
    </section>
  );
};
